import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Globe, Send, Upload, CheckCircle, MessageCircle, QrCode, Loader2, AlertCircle, Clock, ArrowRight } from 'lucide-react';
import { t } from '../utils/translation';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import SectionSplitter from '../components/SectionSplitter';
import Mailik from "@mailik/sdk";

const Contact = ({ theme, language }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', subject: '', message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isDark = theme === 'dark';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await Mailik("3903097e65db40d1").send({
        replyTo: formData.email,
        body: `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Company: ${formData.company}

Message: 
${formData.message}
        `,
        subject: formData.subject || "New Contact Inquiry",
      });

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error(err);
      setError("Failed to send message. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: <Phone size={22} />,
      label: 'Phone / Call',
      value: '+86 180 1328 9913',
      sub: 'Mon–Fri 8:00–18:00 GMT+8',
      href: 'tel:+8618013289913',
      color: '#22c55e',
    },
    {
      icon: <Mail size={22} />,
      label: 'Email',
      value: 'tony@qsconn.com',
      sub: 'Reply within 24 hours',
      href: 'mailto:tony@qsconn.com',
      color: '#3b82f6',
    },
    {
      icon: <MessageCircle size={22} />,
      label: 'WhatsApp',
      value: '+86 139 1322 5243',
      sub: 'Quick responses',
      href: 'https://wa.me/8613913225243',
      color: '#22c55e',
    },
    {
      icon: <QrCode size={22} />,
      label: 'WeChat',
      value: '18013289913',
      sub: 'Scan QR to connect',
      href: null,
      color: '#16a34a',
    },
  ];

  const subjects = [
    t('generalInquiry', language),
    t('productQuote', language),
    t('customManufacturing', language),
    t('technicalSupport', language),
    t('partnershipOpportunity', language),
    t('other', language),
  ];

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: isDark ? '1.5px solid rgba(255,255,255,0.08)' : '1.5px solid rgba(0,0,0,0.1)',
    background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
    color: isDark ? '#f9fafb' : '#111827',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: 88, color: isDark ? '#f9fafb' : '#111827' }}>
      <Helmet>
        <title>Contact Qingshun Electronics — Get a Free Connector Quote</title>
        <meta name="description" content="Contact Qingshun Electronics for product quotes, custom connector manufacturing, or technical support. ISO 9001 certified manufacturer. Reply within 24 hours." />
        <meta name="keywords" content="contact connector manufacturer, get connector quote, Qingshun Electronics contact, custom connector inquiry, wholesale connector price" />
        <link rel="canonical" href="https://qsconn.com/contact" />
      </Helmet>

      {/* Page Hero */}
      <section style={{ padding: '40px 16px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 14px', borderRadius: 999,
            border: '1.5px solid rgba(220,38,38,0.35)',
            background: isDark ? 'rgba(220,38,38,0.1)' : 'rgba(220,38,38,0.07)',
            marginBottom: 18, fontSize: 12, fontWeight: 600, color: '#dc2626', letterSpacing: 0.5,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'breathPulseGlobal 2s infinite' }} />
            We reply within 24 hours
          </div>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 14,
            color: isDark ? '#f87171' : '#b91c1c',
          }}>
            Get in Touch
          </h1>
          <p style={{
            fontSize: '1.05rem', lineHeight: 1.75,
            color: isDark ? '#9ca3af' : '#6b7280',
          }}>
            Share your connector requirements and we'll send you a detailed quote with pricing and compatibility confirmation — usually within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Method Cards */}
      <section style={{ padding: '0 16px 40px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 12,
          }}>
            {contactMethods.map((m, i) => (
              <div key={i}
                style={{
                  padding: '20px 18px', borderRadius: 16,
                  border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                  background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                  boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.25s',
                  cursor: m.href ? 'pointer' : 'default',
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: `${m.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: m.color, marginBottom: 12,
                }}>
                  {m.icon}
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#6b7280' : '#9ca3af', letterSpacing: 0.8, marginBottom: 4, textTransform: 'uppercase' }}>
                  {m.label}
                </div>
                {m.href ? (
                  <a href={m.href} style={{
                    display: 'block', fontSize: 14, fontWeight: 700,
                    color: isDark ? '#f9fafb' : '#111827',
                    textDecoration: 'none', marginBottom: 2,
                  }}>{m.value}</a>
                ) : (
                  <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f9fafb' : '#111827', marginBottom: 2 }}>{m.value}</div>
                )}
                <div style={{ fontSize: 12, color: isDark ? '#6b7280' : '#9ca3af' }}>{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="glow" />

      {/* Main Form + Info */}
      <section style={{ padding: '40px 16px 60px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr)',
            gap: 24,
          }}
            className="contact-grid"
          >
            {/* ── Contact Form ── */}
            <div style={{
              padding: '32px 28px', borderRadius: 20,
              border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
              background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
              boxShadow: isDark ? 'none' : '0 8px 40px rgba(0,0,0,0.06)',
            }}>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{
                  fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', fontWeight: 800,
                  letterSpacing: '-0.02em', marginBottom: 6,
                  color: isDark ? '#f87171' : '#b91c1c',
                }}>
                  Send a Message
                </h2>
                <p style={{ fontSize: 13.5, color: isDark ? '#6b7280' : '#9ca3af' }}>
                  Fill in your details and we'll get back to you shortly.
                </p>
              </div>

              {submitted ? (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: '48px 0', textAlign: 'center',
                }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 16,
                  }}>
                    <CheckCircle size={32} style={{ color: '#22c55e' }} />
                  </div>
                  <h3 style={{ fontSize: 22, fontWeight: 800, color: isDark ? '#f87171' : '#b91c1c', marginBottom: 8 }}>
                    Message Sent!
                  </h3>
                  <p style={{ fontSize: 15, color: isDark ? '#9ca3af' : '#6b7280' }}>
                    Thank you. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  {error && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 16px', borderRadius: 12,
                      background: 'rgba(220,38,38,0.08)',
                      border: '1px solid rgba(220,38,38,0.25)',
                      color: '#dc2626', fontSize: 14,
                    }}>
                      <AlertCircle size={16} />
                      {error}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-grid">
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: isDark ? '#d1d5db' : '#374151' }}>
                        Your Name *
                      </label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required
                        placeholder="John Smith" style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: isDark ? '#d1d5db' : '#374151' }}>
                        Email Address *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required
                        placeholder="john@company.com" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-grid">
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: isDark ? '#d1d5db' : '#374151' }}>
                        Phone
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        placeholder="+1 234 567 8901" style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: isDark ? '#d1d5db' : '#374151' }}>
                        Company
                      </label>
                      <input type="text" name="company" value={formData.company} onChange={handleChange}
                        placeholder="Your Company Name" style={inputStyle} />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: isDark ? '#d1d5db' : '#374151' }}>
                      Subject *
                    </label>
                    <select name="subject" value={formData.subject} onChange={handleChange} required style={inputStyle}>
                      <option value="">Select a topic…</option>
                      {subjects.map((s, i) => <option key={i} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: isDark ? '#d1d5db' : '#374151' }}>
                      Message *
                    </label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5}
                      placeholder="Describe your connector requirements — part number, quantity, application…"
                      style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }} />
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
                      border: isDark ? '1.5px dashed rgba(255,255,255,0.1)' : '1.5px dashed rgba(0,0,0,0.12)',
                      color: isDark ? '#6b7280' : '#9ca3af', fontSize: 13,
                    }}>
                      <Upload size={15} />
                      Attach a file (optional) — max 10 MB
                      <input type="file" style={{ display: 'none' }} />
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px', borderRadius: 14,
                      background: isSubmitting ? 'rgba(220,38,38,0.5)' : 'linear-gradient(135deg, #dc2626, #991b1b)',
                      color: '#fff', fontWeight: 700, fontSize: 15,
                      border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 6px 24px rgba(220,38,38,0.3)',
                      transition: 'all 0.3s',
                      minHeight: 'unset',
                    }}
                  >
                    {isSubmitting ? (
                      <><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Sending…</>
                    ) : (
                      <><Send size={16} /> Send Message</>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* ── Company Info Sidebar ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Address & Hours */}
              <div style={{
                padding: '24px 22px', borderRadius: 20,
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.05)',
              }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: 'rgba(220,38,38,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#dc2626', flexShrink: 0,
                  }}>
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#6b7280' : '#9ca3af', letterSpacing: 0.8, marginBottom: 3, textTransform: 'uppercase' }}>Factory Address</div>
                    <p style={{ fontSize: 13.5, lineHeight: 1.55, color: isDark ? '#d1d5db' : '#374151', margin: 0 }}>
                      2526 Huayuan Road, Zhangpu Town,<br />Kunshan, Suzhou, Jiangsu — China
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: 'rgba(220,38,38,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#dc2626', flexShrink: 0,
                  }}>
                    <Clock size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#6b7280' : '#9ca3af', letterSpacing: 0.8, marginBottom: 3, textTransform: 'uppercase' }}>Business Hours</div>
                    <p style={{ fontSize: 13.5, color: isDark ? '#d1d5db' : '#374151', margin: 0 }}>
                      Monday – Friday<br />8:00 AM – 6:00 PM (GMT+8)
                    </p>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div style={{
                padding: '24px 22px', borderRadius: 20,
                border: isDark ? '1px solid rgba(220,38,38,0.15)' : '1px solid rgba(220,38,38,0.1)',
                background: isDark ? 'rgba(220,38,38,0.05)' : 'rgba(220,38,38,0.03)',
              }}>
                <h3 style={{
                  fontSize: 16, fontWeight: 700, marginBottom: 14,
                  color: isDark ? '#f87171' : '#b91c1c',
                }}>
                  Why Work With Us
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    '50–70% cost savings vs OEM brands',
                    'ISO 9001:2015 & RoHS certified',
                    '14+ years of manufacturing experience',
                    'Direct drop-in replacements for 20+ brands',
                    'Global shipping to 8+ countries',
                    '24h quote turnaround',
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <CheckCircle size={14} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ fontSize: 13.5, lineHeight: 1.5, color: isDark ? '#d1d5db' : '#374151' }}>{item}</span>
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: 18, paddingTop: 16,
                  borderTop: isDark ? '1px solid rgba(220,38,38,0.15)' : '1px solid rgba(220,38,38,0.1)',
                  textAlign: 'center',
                  fontSize: 13, fontWeight: 600, color: '#dc2626',
                }}>
                  Contact us today for a free quote!
                </div>
              </div>

              {/* Quick Contact Links */}
              <div style={{
                padding: '20px 22px', borderRadius: 20,
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                background: isDark ? 'rgba(255,255,255,0.02)' : '#fff',
              }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: isDark ? '#9ca3af' : '#6b7280', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Reach us directly
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'Call / WhatsApp', value: '+86 180 1328 9913', href: 'tel:+8618013289913' },
                    { label: 'Email', value: 'tony@qsconn.com', href: 'mailto:tony@qsconn.com' },
                    { label: 'WhatsApp', value: '+86 139 1322 5243', href: 'https://wa.me/8613913225243' },
                  ].map((link, i) => (
                    <a key={i} href={link.href}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: 10,
                        border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                        background: 'transparent',
                        textDecoration: 'none',
                        transition: 'all 0.2s',
                        minHeight: 'unset', minWidth: 'unset',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, color: isDark ? '#6b7280' : '#9ca3af', fontWeight: 600 }}>{link.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#f9fafb' : '#111827' }}>{link.value}</div>
                      </div>
                      <ArrowRight size={14} style={{ color: '#dc2626', opacity: 0.7 }} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (min-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr 380px !important;
          }
          .form-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 480px) {
          .form-grid {
            grid-template-columns: 1fr !important;
          }
        }
        input:focus, select:focus, textarea:focus {
          border-color: rgba(220,38,38,0.6) !important;
          box-shadow: 0 0 0 3px rgba(220,38,38,0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default Contact;